import React from "react";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import * as dataApi from "services/commonUseDataSetsApi";

const wordCount = (text) =>
  text.trim() ? text.trim().split(/\s+/).length : 0;

export default function CommonUseDataSets({ userInfo = {} }) {
  const auth = useAuth0();
  const history = useHistory();
  const [studies, setStudies] = React.useState([]);
  const [selectedStudy, setSelectedStudy] = React.useState(null);
  const [files, setFiles] = React.useState([]);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [name, setName] = React.useState(
    userInfo.name || (auth.user && auth.user.name) || "",
  );
  const [institution, setInstitution] = React.useState(
    userInfo.institution || "",
  );
  const [abstract, setAbstract] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [authenticationPromptOpen, setAuthenticationPromptOpen] =
    React.useState(false);

  React.useEffect(() => {
    dataApi.listStudies().then(({ data }) => setStudies(data.studies));
  }, []);

  async function openPublicResource(study, resourceIndex) {
    const { data } = await dataApi.getPublicResource(
      study.slug,
      resourceIndex,
    );
    window.open(data.url, "_blank", "noopener,noreferrer");
  }

  async function requestAccess(study) {
    if (!auth.isAuthenticated) {
      setSelectedStudy(study);
      setAuthenticationPromptOpen(true);
      return;
    }

    const { data } = await dataApi.listFiles(
      study.slug,
      auth.getAccessTokenSilently,
    );
    setSelectedStudy(study);
    setFiles(data.files);
    setSelectedFiles([]);
    setNotice("");
  }

  function closeAuthenticationPrompt() {
    setAuthenticationPromptOpen(false);
  }

  async function authenticateForRequest(screenHint) {
    if (!selectedStudy) return;

    await auth.loginWithRedirect({
      appState: {
        returnTo: `/portal/common-use-data-sets?request=${selectedStudy.slug}`,
      },
      prompt: "login",
      ...(screenHint ? { screen_hint: screenHint } : {}),
    });
  }

  React.useEffect(() => {
    if (!auth.isAuthenticated || !studies.length) return;
    const slug = new URLSearchParams(history.location.search).get("request");
    const study = studies.find((item) => item.slug === slug);
    if (study && study.accessMode === "request") requestAccess(study);
  }, [auth.isAuthenticated, studies.length]);

  async function submit(event) {
    event.preventDefault();
    await dataApi.submitRequest(
      {
        studySlug: selectedStudy.slug,
        files: selectedFiles,
        name,
        institution,
        abstract,
      },
      auth.getAccessTokenSilently,
    );
    setNotice("Your request was received and is under review.");
    setFiles([]);
    setSelectedFiles([]);
    setAbstract("");
  }

  if (selectedStudy && files.length) {
    const count = wordCount(abstract);
    return (
      <Card>
        <CardContent>
          <Typography variant="h4">
            Request {selectedStudy.acronym} Access
          </Typography>
          <form onSubmit={submit}>
            <Typography variant="h6">Choose data files</Typography>
            {files.map((file) => (
              <FormControlLabel
                key={file.key}
                control={
                  <Checkbox
                    checked={selectedFiles.includes(file.key)}
                    onChange={() =>
                      setSelectedFiles((current) =>
                        current.includes(file.key)
                          ? current.filter((key) => key !== file.key)
                          : [...current, file.key],
                      )
                    }
                  />
                }
                label={file.name}
              />
            ))}
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              required
              label="Institutional affiliation"
              value={institution}
              onChange={(event) => setInstitution(event.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              required
              multiline
              minRows={8}
              label="Abstract / rationale"
              value={abstract}
              onChange={(event) => setAbstract(event.target.value)}
              margin="normal"
              helperText={`${count} / 1,000 words`}
            />
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={
                !selectedFiles.length ||
                !name.trim() ||
                !institution.trim() ||
                count < 1 ||
                count > 1000
              }
            >
              Submit request
            </Button>{" "}
            <Button onClick={() => setFiles([])}>Cancel</Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Dialog
        open={authenticationPromptOpen}
        onClose={closeAuthenticationPrompt}
        aria-labelledby="common-use-authentication-title"
      >
        <DialogTitle id="common-use-authentication-title">
          Account Required
        </DialogTitle>
        <DialogContent>
          <Typography>
            You must log in or create a PREVENT data-request account before
            requesting access. Creating an account does not grant access to
            any data or PREVENT member features.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAuthenticationPrompt}>Cancel</Button>
          <Button color="primary" onClick={() => authenticateForRequest()}>
            Log In
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => authenticateForRequest("signup")}
          >
            Create Account
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant="h3">Common Use Data Sets</Typography>
      {notice && <Typography color="primary">{notice}</Typography>}
      <Grid container spacing={3}>
        {studies.map((study) => (
          <Grid item xs={12} md={6} key={study.slug}>
            <Card>
              <CardContent>
                <Typography variant="h5">{study.acronym}</Typography>
                <Typography variant="h6">{study.name}</Typography>
                <Typography>{study.description}</Typography>
                {study.publicResources.map((resource, resourceIndex) => (
                  <div key={resource.key} style={{ marginTop: 12 }}>
                    <Typography>{resource.name}</Typography>
                    <Button
                      color="primary"
                      onClick={() =>
                        openPublicResource(study, resourceIndex)
                      }
                    >
                      {resource.downloadable ? "Open / Download" : "View"}
                    </Button>
                  </div>
                ))}
              </CardContent>
              {study.accessMode === "request" && (
                <CardActions>
                  <Button
                    color="primary"
                    onClick={() => requestAccess(study)}
                  >
                    Request Access
                  </Button>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

CommonUseDataSets.propTypes = {
  userInfo: PropTypes.shape({
    name: PropTypes.string,
    institution: PropTypes.string,
  }),
};
